import { X } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDateRangeField } from "@/shared/components/forms/ColocaDateRangeField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";

import type { CardWalletMovementsFilters as Filters } from "../../types/cardWallets.types";
import { DEFAULT_MOVEMENTS_FILTERS, hasActiveMovementFilters } from "../../utils/cardWalletMovements";

const filtersSchema = z.object({
  transaction_type: z.enum([
    "all",
    "TOPUP",
    "TRANSFER",
    "WITHDRAWAL",
    "FEE",
    "DEPOSIT",
    "REVERSAL",
    "ADJUSTMENT",
  ]),
  direction: z.enum(["all", "in", "out"]),
  from: z.string(),
  to: z.string(),
});

export interface CardWalletMovementsFiltersProps {
  filters: Filters;
  onChange: (next: Filters) => void;
  onClear: () => void;
}

/** Server-side filters of the statement: type, direction and a `created_at` day range. */
export function CardWalletMovementsFilters({ filters, onChange, onClear }: CardWalletMovementsFiltersProps) {
  const { t } = useTranslations("cardWallets");
  const form = useZodForm(filtersSchema, filters);

  useEffect(() => {
    const subscription = form.watch((value) => onChange(value as Filters));
    return () => subscription.unsubscribe();
  }, [form, onChange]);

  const typeItems = [
    { value: "all", label: `${t("filter_type")} · ${t("filter_type_all")}` },
    { value: "TOPUP", label: t("tx_type_topup") },
    { value: "TRANSFER", label: t("tx_type_transfer") },
    { value: "WITHDRAWAL", label: t("tx_type_withdrawal") },
    { value: "FEE", label: t("tx_type_fee") },
    { value: "DEPOSIT", label: t("tx_type_deposit") },
    { value: "REVERSAL", label: t("tx_type_reversal") },
    { value: "ADJUSTMENT", label: t("tx_type_adjustment") },
  ];
  const directionItems = [
    { value: "all", label: `${t("filter_direction")} · ${t("filter_direction_all")}` },
    { value: "in", label: t("filter_direction_in") },
    { value: "out", label: t("filter_direction_out") },
  ];

  return (
    <ColocaForm
      form={form}
      onSubmit={() => {
        // No-op: the filters are applied live through `form.watch`. The <form> only stops Enter reloading.
      }}
      className="flex w-full flex-col gap-2 space-y-0 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center"
    >
      <div className="w-full sm:w-[180px]">
        <ColocaSelectField name="transaction_type" items={typeItems} />
      </div>
      <div className="w-full sm:w-[170px]">
        <ColocaSelectField name="direction" items={directionItems} />
      </div>
      <div className="w-full sm:w-[230px]">
        <ColocaDateRangeField
          nameFrom="from"
          nameTo="to"
          placeholder={t("filter_dates")}
          maxDate={new Date()}
        />
      </div>
      {hasActiveMovementFilters(filters) && (
        <ColocaButton
          type="button"
          variant="ghost"
          size="sm"
          icon={X}
          onClick={() => {
            form.reset(DEFAULT_MOVEMENTS_FILTERS);
            onClear();
          }}
        >
          {t("filter_clear")}
        </ColocaButton>
      )}
    </ColocaForm>
  );
}
